import { featuredComics } from './home';
import { categoryComics } from './category';

type ArticleDetail = {
  id: number;
  title: string;
  author: string;
  category: string;
  categorySlug: string;
  coverImage: string;
  description: string;
  longDescription: string;
  chapters: number;
  views: string;
  rating: number;
  status: string;
  lastUpdated: string;
  tags: string[];
  chapterList: { number: number; title: string; date: string; }[];
};

const buildChapterList = (count: number) => Array.from({ length: count }, (_, i) => ({
  number: count - i,
  title: `第${count - i}话`,
  date: `2024-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((30 - (i % 30)) || 30).padStart(2, '0')}`,
}));

const generatedArticleDetails = Object.fromEntries(
  categoryComics.map((comic) => {
    const description = comic.description || `${comic.title}是一部${comic.category}题材漫画，剧情紧凑、画风鲜明，适合喜欢高品质连载作品的读者。`;
    return [String(comic.id), {
      id: comic.id,
      title: comic.title,
      author: comic.author,
      category: comic.category,
      categorySlug: comic.categorySlug,
      coverImage: comic.coverImage,
      description,
      longDescription: `${description}\n\n作品围绕主角的成长、选择与冒险展开，在持续更新的章节中呈现更完整的世界观和人物关系。`,
      chapters: comic.chapters,
      views: comic.views,
      rating: comic.rating,
      status: comic.isNew ? '连载中' : '连载中',
      lastUpdated: '2024-07-03',
      tags: comic.tags,
      chapterList: buildChapterList(comic.chapters),
    }];
  })
) as Record<string, ArticleDetail>;

export const articleDetail: Record<string, ArticleDetail> = {
  ...generatedArticleDetails,
  '1': {
    id: 1,
    title: '洞洞杂货铺',
    author: '墨白',
    category: '奇幻冒险',
    categorySlug: 'fantasy',
    coverImage: featuredComics[0].coverImage,
    description: '一家隐藏在城市角落的神秘杂货铺，每个物品都有它自己的故事。店主洞爷用温暖和智慧帮助每一个走进店铺的人。',
    longDescription: '在城市深处的一条不起眼的小巷里，有一家名为"洞洞杂货铺"的古老店铺。这家店白天从不营业，只在午夜时分才会亮起温暖的灯光。店主洞爷是一位看起来永远30岁的神秘男子，他的货架上摆满了各种各样奇怪的物品——会说话的收音机、能看到未来的镜子、能让人一夜学会任何技能的古书...\n\n每一个走进杂货铺的人，都会在这里找到他们最需要的东西——不一定是最想要的，但一定是最需要的。洞爷用他独特的方式，帮助迷茫的人们找到人生的方向，治愈受伤的心灵。当城市陷入黑暗，这家小店就会成为最温暖的光。',
    chapters: 186,
    views: '1280万',
    rating: 9.7,
    status: '连载中',
    lastUpdated: '2024-07-03',
    tags: ['热门', '推荐', '奇幻', '治愈'],
    chapterList: Array.from({ length: 186 }, (_, i) => ({
      number: 186 - i,
      title: 186 - i === 186 ? `第${186 - i}话 最后的杂货` : `第${186 - i}话`,
      date: `2024-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((30 - (i % 30)) || 30).padStart(2, '0')}`,
    })),
  },
  '2': {
    id: 2,
    title: '星海迷途',
    author: '林深',
    category: '科幻未来',
    categorySlug: 'scifi',
    coverImage: featuredComics[1].coverImage,
    description: '在未来星际时代，一位迷航的宇航员在未知星系中追寻归途的故事。',
    longDescription: '公元2350年，人类已经殖民了大半个银河系。年轻的宇航员苏星辰在一次深空探索任务中遭遇意外，漂流到了一个人类从未涉足的未知星系。这里有着令人叹为观止的星云美景，也隐藏着古老外星文明留下的秘密。\n\n在寻找归途的过程中，苏星辰发现了一个足以改变人类文明进程的真相。她必须在回家与守护秘密之间做出选择。',
    chapters: 92,
    views: '650万',
    rating: 9.5,
    status: '连载中',
    lastUpdated: '2024-07-02',
    tags: ['新作', '科幻', '冒险'],
    chapterList: Array.from({ length: 92 }, (_, i) => ({
      number: 92 - i,
      title: `第${92 - i}话`,
      date: `2024-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((30 - (i % 30)) || 30).padStart(2, '0')}`,
    })),
  },
  '3': {
    id: 3,
    title: '长安妖事录',
    author: '苏九',
    category: '古风仙侠',
    categorySlug: 'xianxia',
    coverImage: featuredComics[2].coverImage,
    description: '大唐长安，人妖共存。一位擅长画妖的画师，用画笔记录下长安城中每一段妖异而动人的故事。',
    longDescription: '开元盛世的长安城，繁华之下暗流涌动。人界与妖界的界限在这座万国来朝的大都市中变得模糊不清。画师白素卿拥有一双能看见妖怪的眼睛，以及能将妖怪封印在画中的神奇画笔。\n\n每一幅画的背后，都是一段人与妖之间的爱恨纠葛。有为了报恩化身美人的狐妖，有守护古寺千年的石狮精，也有隐藏在深宫中的龙女...白素卿在画尽长安妖事的同时，也渐渐发现自己身世中隐藏的秘密。',
    chapters: 154,
    views: '960万',
    rating: 9.8,
    status: '连载中',
    lastUpdated: '2024-07-01',
    tags: ['经典', '仙侠', '古风'],
    chapterList: Array.from({ length: 154 }, (_, i) => ({
      number: 154 - i,
      title: 154 - i === 154 ? `第${154 - i}话 月下逢` : `第${154 - i}话`,
      date: `2024-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((30 - (i % 30)) || 30).padStart(2, '0')}`,
    })),
  },
  '4': {
    id: 4,
    title: '雨夜追踪',
    author: '七月',
    category: '悬疑推理',
    categorySlug: 'mystery',
    coverImage: featuredComics[3].coverImage,
    description: '连环案件发生在大雨滂沱的城市夜晚，年轻刑警在雨幕中追查真相。',
    longDescription: '在一座常年多雨的城市里，每当暴雨来临，就会发生一起离奇案件。作案手法相似，却找不到任何关联点。年轻刑警陈墨深接手此案后，发现所有线索都指向了一个十年前已经结案的旧案。\n\n雨夜中，真相如雨丝般若隐若现。当陈墨深越接近真相，危险也离他越来越近。原来，有些人一直在等待着这场雨，等待了整整十年。',
    chapters: 78,
    views: '430万',
    rating: 9.4,
    status: '连载中',
    lastUpdated: '2024-06-28',
    tags: ['悬疑', '新作', '推理'],
    chapterList: Array.from({ length: 78 }, (_, i) => ({
      number: 78 - i,
      title: 78 - i === 78 ? `第${78 - i}话 真相` : `第${78 - i}话`,
      date: `2024-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((30 - (i % 30)) || 30).padStart(2, '0')}`,
    })),
  },
};

export const defaultArticleDetail = {
  id: 1,
  title: '洞洞杂货铺',
  author: '墨白',
  category: '奇幻冒险',
  categorySlug: 'fantasy',
  coverImage: featuredComics[0].coverImage,
  description: '一家隐藏在城市角落的神秘杂货铺，每个物品都有它自己的故事。',
  longDescription: '在城市深处的一条不起眼的小巷里，有一家名为"洞洞杂货铺"的古老店铺...',
  chapters: 186,
  views: '1280万',
  rating: 9.7,
  status: '连载中',
  lastUpdated: '2024-07-03',
  tags: ['热门', '推荐', '奇幻', '治愈'],
  chapterList: Array.from({ length: 186 }, (_, i) => ({
    number: 186 - i,
    title: `第${186 - i}话`,
    date: `2024-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((30 - (i % 30)) || 30).padStart(2, '0')}`,
  })),
};

export const relatedArticles = [
  {
    id: 7,
    title: '剑来山海',
    author: '墨白',
    category: '热血战斗',
    coverImage: featuredComics[6].coverImage,
    chapters: 132,
    rating: 9.7,
  },
  {
    id: 3,
    title: '长安妖事录',
    author: '苏九',
    category: '古风仙侠',
    coverImage: featuredComics[2].coverImage,
    chapters: 154,
    rating: 9.8,
  },
  {
    id: 13,
    title: '山海异闻录',
    author: '苏九',
    category: '奇幻冒险',
    coverImage: 'https://readdy.ai/api/search-image?query=Chinese%20fantasy%20manga%20cover%2C%20mystical%20creatures%20from%20Chinese%20mythology%20roaming%20ancient%20mountain%20landscape%2C%20phoenix%20and%20nine-tailed%20fox%20in%20dramatic%20lighting%2C%20rich%20traditional%20art%20influence%20with%20modern%20manga%20style%2C%20clean%20atmospheric%20background%2C%20professional%20manga%20cover&width=400&height=560&seq=comic-related-shanhai-01&orientation=portrait',
    chapters: 98,
    rating: 9.5,
  },
  {
    id: 9,
    title: '星辰变',
    author: '天蚕',
    category: '古风仙侠',
    coverImage: 'https://readdy.ai/api/search-image?query=Chinese%20xianxia%20manga%20cover%2C%20young%20cultivator%20ascending%20to%20heavens%20with%20dramatic%20spiritual%20energy%2C%20ancient%20Chinese%20cosmic%20landscape%2C%20swirling%20clouds%20and%20stars%2C%20majestic%20and%20epic%20illustration%20style%2C%20clean%20gradient%20background%2C%20professional%20manga%20cover&width=400&height=560&seq=comic-related-star-02&orientation=portrait',
    chapters: 312,
    rating: 9.6,
  },
];
